import { useEffect, useState } from "react";
import { DEFAULT_PAGE_SIZE } from "../../configs/constants";
import Preference from "../UI/Preference";
import Pagination from "../UI/Pagination";
import { getHeaderCounterText, getPagedItems } from "../../utils/common";
import {
  Table as CSDTable,
  Box,
  Header,
  SpaceBetween,
} from "@cloudscape-design/components-themed";
import { Link } from "react-router-dom";
import type { RootState } from "../../app/store";
import { useAppSelector } from "../../app/hook";
import { COMMON_LABELS } from "../default-constants";
import { LABELS } from "./constants";
import { useGetCounselingListQuery } from "../../features/api";
import { CounselingSearchResponse } from "../../features/api-interfaces";

const columnDefinitions = [
  {
    id: "negotiationStatus",
    header: LABELS.NEGOTIATION_STATUS,
    cell: (e: any) => e.negotiationStatusLabel,
  },
  {
    id: "corporateNumber",
    header: LABELS.CORPORATE_NUMBER,
    cell: (e: any) => e.corporateNumber,
  },
  {
    id: "companyName",
    header: LABELS.COMPANY_NAME,
    cell: (e: any) => (
      // 個別相談案件登録・更新
      <Link to={"/individual-counseling-project/" + e.individualConsultationNo}>
        {e.companyName}
      </Link>
    ),
  },
];

/**
 * 個別相談案件一覧
 */
const Table = () => {
  const searchCondition = useAppSelector(
    (state: RootState) => state.counselingSearchCondition
  );

  // 一覧データの取得
  const { data, isFetching, refetch } =
    useGetCounselingListQuery(searchCondition);

  // 一覧データの加工
  const listData = data
    ? data.map((item: CounselingSearchResponse) => ({
        ...item,
        negotiationStatusLabel:
          item.negotiationStatus === 1
            ? LABELS.NEGOTIATION_COMPLETE
            : LABELS.IN_NEGOTIATION,
      }))
    : [];

  /**ページャ関連 */
  const options = columnDefinitions.map((column) => ({
    id: column.id,
    label: column.header,
  }));
  const [currentPageIndex, setCurrentPageIndex] = useState(1);
  const [preferences, setPreferences] = useState({
    pageSize: DEFAULT_PAGE_SIZE,
    visibleContent: options.map((o) => o.id),
  });

  const [pagedItems, setPagedItems] = useState<Object[]>([]);
  const [allItems, setAllItems] = useState<Object[]>([]);
  useEffect(() => {
    if (listData !== undefined) {
      setPagedItems(getPagedItems(listData, 1, preferences.pageSize));
      setAllItems(listData);
      setCurrentPageIndex(1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  useEffect(() => {
    refetch();
  }, [refetch]);

  /** 設定変更関数 */
  const handleOnConfirm = (detail: any) => {
    const newPageSize = detail.pageSize;
    setPreferences({
      pageSize: newPageSize,
      visibleContent: detail.visibleContent,
    });
    setPagedItems(listData.slice(0, newPageSize));
    setCurrentPageIndex(1);
  };

  return (
    <CSDTable
      columnDefinitions={columnDefinitions}
      loading={isFetching}
      loadingText={COMMON_LABELS.CSD.LOADING_TEXT}
      items={pagedItems}
      resizableColumns
      stickyHeader
      header={
        <SpaceBetween size="xs">
          <Header>
            {LABELS.TITLE}
            {getHeaderCounterText(allItems.length, pagedItems.length)}
          </Header>
        </SpaceBetween>
      }
      pagination={
        <Pagination
          currentPageIndex={currentPageIndex}
          filteredItems={allItems}
          setCurrentPageIndex={setCurrentPageIndex}
          pageSize={preferences.pageSize}
          setPagedItems={setPagedItems}
        />
      }
      preferences={
        <Preference
          preferences={preferences}
          options={options}
          handleOnConfirm={handleOnConfirm}
        />
      }
      empty={
        <Box textAlign="center" color="inherit">
          {COMMON_LABELS.CSD.NO_DATA}
        </Box>
      }
    />
  );
};

export default Table;
